import type { Survey, SurveyStatus } from 'types';
import { useQuery } from '@tanstack/react-query';
import { useClient } from 'hooks/useClient';
import { useQueryState } from 'hooks/useQueryState';
import { useWebsites } from '@/state/useWebsites';
import { SURVEY_STATUSES } from 'definitions';

const VIEWS = [...SURVEY_STATUSES, 'All', 'Archived'] as const;
export type SurveyListView = (typeof VIEWS)[number];

// Running first, then Draft, then Ended: the order someone actually cares about.
const statusRank: Record<SurveyStatus, number> = { Running: 0, Draft: 1, Ended: 2 };

export const useSurveysList = () => {
  const [view, setView] = useQueryState<SurveyListView>('view', 'All');
  const apiClient = useClient();
  const { website } = useWebsites();
  const websiteId = website?.id || '';

  const { data: allSurveys, isLoading } = useQuery<Survey[]>({
    queryKey: ['SURVEYS', websiteId],
    enabled: !!websiteId,
    queryFn: async () => apiClient.get<Survey[]>(`/surveys/website/${websiteId}`),
  });

  // Derived in the hook, per CONVENTIONS — components stay render-only.
  const surveys = (() => {
    if (!allSurveys) return [];
    const byView =
      view === 'All'
        ? allSurveys.filter((survey) => !survey.archived)
        : view === 'Archived'
          ? allSurveys.filter((survey) => !!survey.archived)
          : allSurveys.filter((survey) => survey.status === view && !survey.archived);

    return [...byView].sort(
      (a, b) => statusRank[a.status] - statusRank[b.status] || (b.createdAt ?? 0) - (a.createdAt ?? 0),
    );
  })();

  const counts = (() => {
    const live = allSurveys?.filter((survey) => !survey.archived) ?? [];
    return {
      // `total` counts archived ones too, because it answers a different question
      // than `all` does: whether this website has ever had a survey. Archiving the
      // last one should not put the first-run explainer back on the screen.
      total: allSurveys?.length ?? 0,
      all: live.length,
      running: live.filter((survey) => survey.status === 'Running').length,
      draft: live.filter((survey) => survey.status === 'Draft').length,
    };
  })();

  return { view, setView, views: VIEWS, surveys, counts, isLoading, hasWebsite: !!websiteId };
};

export type SurveysListManager = ReturnType<typeof useSurveysList>;
